import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { DirectiveSuperclass } from '@sama/ng-core';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'h3-feedback-item',
  templateUrl: './feedback-item.component.html',
  styleUrls: ['./feedback-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatMenuModule],
})
export class FeedbackItemComponent
  extends DirectiveSuperclass
  implements AfterViewInit
{
  @Input({ required: true }) title!: string;
  @Input() subTitle?: string;
  @Input({ required: true }) isEditAllowed!: boolean;
  @Input({ required: true }) isDeleteAllowed!: boolean;
  @Input({ required: true }) timestamp!: string;
  @Input() comment?: string;
  @Input() @HostBinding('class.selected') selected?: boolean;
  @Output() edit = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() select = new EventEmitter<void>();

  @ViewChild('commentElement')
  private commentElement?: ElementRef<HTMLElement>;

  isCommentTruncated$ = new BehaviorSubject(false);
  showFullComment = false;

  private elementRef = inject(ElementRef);
  private preventScrollIntoView = false;

  @HostListener('click')
  selectItem() {
    // we let the parent handle the selection, but we want to prevent scrolling
    // into view since the item is already in view
    this.preventScrollIntoView = true;
    this.select.emit();
  }

  ngAfterViewInit() {
    const isCommentOverflowing =
      this.commentElement &&
      this.commentElement.nativeElement.scrollHeight >
        this.commentElement.nativeElement.clientHeight;

    if (isCommentOverflowing) {
      setTimeout(() => {
        this.isCommentTruncated$.next(true);
      }, 0);
    }

    this.setUpScrollIntoViewOnSelection();
  }

  editFeedback() {
    this.edit.emit();
  }

  deleteFeedback(event: MouseEvent, trigger: MatMenuTrigger) {
    // for some reason, specifically for delete if the feedback item is inside
    // another mat menu, it also closes the parent mat menu, stopping
    // propagation and manually closing this menu fixes it
    event.stopPropagation();
    this.delete.emit();
    trigger.closeMenu();
  }

  toggleShowFullComment() {
    this.showFullComment = !this.showFullComment;
  }

  private setUpScrollIntoViewOnSelection() {
    this.subscribeTo(this.getInput$('selected'), (isSelected) => {
      if (isSelected && !this.preventScrollIntoView) {
        this.elementRef.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      this.preventScrollIntoView = false;
    });
  }
}
